import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Paper, IconButton, 
  Tabs, Tab, Badge, Fade, CircularProgress, Tooltip,
  Avatar, Button, Divider, Stack
} from '@mui/material';
import { 
  ArrowBack, DoneAll, NotificationsOff, DeleteOutline, 
  Check, InfoOutlined, TaskAlt, WarningAmber, 
  ErrorOutline, AccountBalanceWallet, Brush, Settings,
  Circle
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

// --- Premium Theme Config ---
const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
const lightBg = '#F8FAFC';

// --- Helper: Time Ago ---
const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-IN');
};

// 🌟 DUMMY DATA (Replace with API) 🌟
const initialNotifications = [
  { id: '1', type: 'credit', title: 'क्रेडिट सफलता से जोड़े गए', message: 'आपके वॉलेट में 500 नए क्रेडिट जोड़ दिए गए हैं।', created_at: new Date(Date.now() - 5 * 60000).toISOString(), is_read: false },
  { id: '2', type: 'design', title: 'आपका डिज़ाइन तैयार है!', message: 'आपका "होली मिलन" पोस्टर डाउनलोड के लिए तैयार है।', created_at: new Date(Date.now() - 3600000).toISOString(), is_read: false },
  { id: '3', type: 'warning', title: 'बैलेंस कम है', message: 'आपके वॉलेट में केवल 10 क्रेडिट बचे हैं। कृपया रिचार्ज करें।', created_at: new Date(Date.now() - 86400000).toISOString(), is_read: true },
  { id: '4', type: 'success', title: 'प्लान अपग्रेड सफल', message: 'आपका खाता "गोल्ड प्लान" में सफलतापूर्वक अपग्रेड कर दिया गया है।', created_at: new Date(Date.now() - 172800000).toISOString(), is_read: true },
  { id: '5', type: 'system', title: 'सिस्टम अपडेट', message: 'हमने ऐप में नए चुनावी टेम्प्लेट जोड़े हैं। अभी चेक करें!', created_at: new Date(Date.now() - 259200000).toISOString(), is_read: true },
];

export default function Notifications() {
  const navigate = useNavigate();
  
  // States
  const [tabValue, setTabValue] = useState(0); // 0 = All, 1 = Unread
  const [notifications, setNotifications] = useState(initialNotifications);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Simulated API Actions
  const handleMarkAsRead = (id, e) => {
    e.stopPropagation();
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    // Call API: fetch(`/api/notifications/${id}/read`, { method: 'PUT' })
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    // Call API: fetch('/api/notifications/mark-all-read', { method: 'PUT' })
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== id));
    // Call API: fetch(`/api/notifications/${id}`, { method: 'DELETE' })
  };

  const handleNotificationClick = (id, isRead) => {
    if (!isRead) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    }
    // Handle specific navigation if needed based on notification type
  };

  const loadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setLoadingMore(false);
      // Append more dummy data or call API
    }, 1000);
  };

  // Dynamic Icon & Colors based on Type
  const getIconMeta = (type) => {
    switch(type) {
      case 'success': return { icon: <TaskAlt />, color: '#10B981', bg: '#ECFDF5' };
      case 'warning': return { icon: <WarningAmber />, color: '#F59E0B', bg: '#FFFBEB' };
      case 'error': return { icon: <ErrorOutline />, color: '#EF4444', bg: '#FEF2F2' };
      case 'credit': return { icon: <AccountBalanceWallet />, color: '#3B82F6', bg: '#EFF6FF' };
      case 'design': return { icon: <Brush />, color: '#EC4899', bg: '#FDF2F8' };
      case 'system': return { icon: <Settings />, color: '#64748B', bg: '#F1F5F9' };
      default: return { icon: <InfoOutlined />, color: '#64748B', bg: '#F1F5F9' };
    }
  };

  // Filter based on Tab
  const displayedNotifications = notifications.filter(n => tabValue === 0 || !n.is_read);

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 10 }}>
      
      {/* 🌟 STICKY HEADER 🌟 */}
      <Box 
        sx={{ 
          background: primaryGradient, 
          position: 'sticky', top: 0, zIndex: 1100, 
          boxShadow: '0 4px 20px rgba(238, 9, 121, 0.2)' 
        }}
      >
        <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton component={Link} to="/my-profile" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" fontWeight="800" color="white">
                सूचनाएं (Notifications)
              </Typography>
            </Box>

            <Tooltip title="Mark all as read" arrow>
              <IconButton 
                onClick={handleMarkAllAsRead} 
                disabled={unreadCount === 0}
                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', bgcolor: 'transparent' } }}
              >
                <DoneAll />
              </IconButton>
            </Tooltip>
          </Box>

          {/* 🌟 TABS 🌟 */}
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{ 
              minHeight: '48px',
              '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', fontWeight: '700', textTransform: 'none', fontSize: '1rem', py: 1.5 },
              '& .Mui-selected': { color: 'white !important' },
              '& .MuiTabs-indicator': { backgroundColor: 'white', height: 3, borderTopLeftRadius: 3, borderTopRightRadius: 3 }
            }}
          >
            <Tab label="All / सभी" />
            <Tab label={
              <Badge badgeContent={unreadCount} sx={{ '& .MuiBadge-badge': { bgcolor: 'white', color: '#EE0979', fontWeight: 'bold' } }}>
                Unread / अपठित <Box component="span" sx={{ width: 16 }} /> {/* spacing for badge */}
              </Badge>
            } />
          </Tabs>
        </Container>
      </Box>

      {/* 🌟 NOTIFICATIONS LIST 🌟 */}
      <Container maxWidth="sm" sx={{ mt: 3, px: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box textAlign="center" py={10}>
            <CircularProgress sx={{ color: '#FF6A00' }} />
            <Typography variant="body2" color="#64748B" mt={2}>Loading notifications...</Typography>
          </Box>
        ) : displayedNotifications.length === 0 ? (
          <Box textAlign="center" py={10}>
            <NotificationsOff sx={{ fontSize: 60, color: '#CBD5E1', mb: 2 }} />
            <Typography variant="h6" fontWeight="800" color="#1E293B">कोई सूचना नहीं</Typography>
            <Typography variant="body2" color="#64748B">You're all caught up! No notifications here.</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {displayedNotifications.map((notif, index) => {
              const meta = getIconMeta(notif.type);
              
              return (
                <Fade in={true} timeout={(index + 1) * 200} key={notif.id}>
                  <Paper 
                    elevation={0}
                    onClick={() => handleNotificationClick(notif.id, notif.is_read)}
                    sx={{ 
                      p: 2, borderRadius: 3, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                      border: '1px solid', borderColor: notif.is_read ? '#E2E8F0' : '#FFDED0', // Highlight border for unread
                      borderLeft: notif.is_read ? '1px solid #E2E8F0' : '4px solid #FF6A00', // Thick left border for unread
                      bgcolor: notif.is_read ? 'white' : '#FFF9F6', // Subtle tint for unread
                      transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)' }
                    }}
                  >
                    {/* Unread Dot Indicator */}
                    {!notif.is_read && (
                      <Circle sx={{ color: '#FF6A00', fontSize: 10, position: 'absolute', top: 16, right: 16 }} />
                    )}

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {/* Avatar Icon */}
                      <Avatar sx={{ bgcolor: meta.bg, color: meta.color, width: 48, height: 48 }}>
                        {meta.icon}
                      </Avatar>
                      
                      {/* Content */}
                      <Box sx={{ flex: 1, pr: !notif.is_read ? 2 : 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight="800" color="#1E293B" sx={{ pr: 1, lineHeight: 1.3 }}>
                            {notif.title}
                          </Typography>
                          <Typography variant="caption" color="#94A3B8" sx={{ whiteSpace: 'nowrap', fontWeight: '600' }}>
                            {getTimeAgo(notif.created_at)}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color={notif.is_read ? "#64748B" : "#475569"} sx={{ mb: 1.5, lineHeight: 1.5 }}>
                          {notif.message}
                        </Typography>

                        {/* Action Buttons Row */}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {!notif.is_read && (
                            <Tooltip title="Mark as read" arrow>
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleMarkAsRead(notif.id, e)}
                                sx={{ bgcolor: '#F1F5F9', color: '#10B981', '&:hover': { bgcolor: '#ECFDF5' } }}
                              >
                                <Check fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete" arrow>
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleDelete(notif.id, e)}
                              sx={{ bgcolor: '#F1F5F9', color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Fade>
              );
            })}
          </Stack>
        )}

        {/* Load More Button */}
        {displayedNotifications.length > 0 && (
          <Box textAlign="center" mt={4}>
            <Button 
              variant="outlined" 
              onClick={loadMore}
              disabled={loadingMore}
              sx={{ 
                borderRadius: 50, px: 4, fontWeight: 'bold', color: '#475569', borderColor: '#CBD5E1',
                '&:hover': { bgcolor: '#F1F5F9', borderColor: '#94A3B8' }
              }}
            >
              {loadingMore ? <CircularProgress size={20} color="inherit" /> : 'Load More / और लोड करें'}
            </Button>
          </Box>
        )}

      </Container>
    </Box>
  );
}